import { BILLING_PERIODS } from '@uit/shared/billing-period';
import { type BillingRunsResponse, BillingRunsResponse as BillingRunsResponseSchema } from '@uit/shared/schemas/billing';
import { type Client, Client as ClientSchema } from '@uit/shared/schemas/client';
import {
  type QbConfirmRatesInput,
  QbConfirmRatesResponse,
  type QbPushSummary,
  type QbReadiness,
  type QbSkipResourceInput,
  type QbUnskipResourceInput,
  RATE_CHANGE_HOLD_PCT,
  type RateField,
} from '@uit/shared/schemas/quickbooks';
import { type VendorPickerResponse, VendorPickerResponse as VendorPickerResponseSchema } from '@uit/shared/schemas/vendor';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { RunReportsPanel } from '../components/billing/RunReportsPanel.js';
import { type LinkScope, LinkReviewDialog } from '../components/quickbooks/LinkReviewDialog.js';
import { apiMutation, useApi } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useFeatureFlag } from '../lib/feature-flags.js';
import s from './BillingPage.module.scss';

const ClientList = z.array(ClientSchema);

interface FlaggedRow {
  row_number: number;
  worker: string;
  problems: string[];
}
interface SkippedRow {
  row_number: number;
  worker: string;
  reason: string;
}
interface PreviewResult {
  period: string;
  rows_in: number;
  rows_ready: number;
  rows_skipped: number;
  rows_flagged: number;
  flagged: FlaggedRow[];
  skipped: SkippedRow[];
  // Push pre-flight (T-205); null/absent when the qb_bill_push flag is off.
  qb_readiness?: QbReadiness | null;
}

type PushResult = QbPushSummary;
/** One rate change an admin can confirm — from the readiness card or the push result. */
interface ConfirmRow {
  engagement_id: string;
  field: RateField;
  qb_rate: number;
  worker: string;
}
type PushAbort = NonNullable<PushResult['aborted']>;

/** Whether a push response body carries the run summary (T-207: even 409/500 do). */
function isPushResult(body: unknown): body is PushResult {
  return typeof body === 'object' && body !== null && typeof (body as PushResult).pushed === 'number';
}

const FIELD_LABEL: Record<RateField, string> = {
  rate_to_contractor: 'Contractor rate',
  rate_to_client: 'Client rate',
};

const money = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

function pctText(pct: number): string {
  const p = Math.round(pct * 10) / 10;
  return `${p > 0 ? '+' : ''}${p}%`;
}

/** Client-rate entries are owner-only: a non-owner only ever gets these counts (T-210). */
function hiddenClientRatesNote(changes: number, fills: number): string | null {
  const parts: string[] = [];
  if (changes > 0) parts.push(`${changes} client-rate ${changes === 1 ? 'change' : 'changes'}`);
  if (fills > 0) parts.push(`${fills} client-rate ${fills === 1 ? 'fill' : 'fills'}`);
  return parts.length > 0 ? `${parts.join(' and ')} ${changes + fills === 1 ? 'is' : 'are'} visible to the owner only.` : null;
}

const ABORT_COPY: Record<PushAbort['reason'], string> = {
  quickbooks_not_connected:
    'QuickBooks disconnected partway through. Reconnect it in Settings, then push again.',
  rate_limited: 'QuickBooks asked us to slow down. Wait a minute, then push again.',
  terms_not_found:
    'QuickBooks has no payment Term named like the report\'s Sales Term (Net 30), so nothing was pushed. Add that Term in QuickBooks, then push again.',
  internal_error: 'The push stopped on an unexpected error. Bills already pushed are safe — push again.',
};

interface Resolution {
  skip: boolean;
  reason: string;
  product_service: string;
  // Resolve an unknown worker by naming their vendor + client (T-064) — the
  // server materializes an incomplete vendor + resource + engagement. The vendor
  // name is backed by a datalist of existing vendors; the server reuses a
  // same-named vendor (case-insensitive) instead of duplicating it.
  vendor_name: string;
  client_id: string;
  // Admin-typed Worker ID for a missing worker whose VMS row has none (T-069).
  worker_id: string;
}

const EMPTY_RES: Resolution = {
  skip: false,
  reason: '',
  product_service: '',
  vendor_name: '',
  client_id: '',
  worker_id: '',
};

function isAddressed(r: Resolution | undefined): boolean {
  if (!r) return false;
  if (r.skip) return r.reason.trim() !== '';
  // Either supply a missing service, or map the worker to a vendor + client.
  return (
    r.product_service.trim() !== '' || (r.vendor_name.trim() !== '' && r.client_id.trim() !== '')
  );
}

export function BillingPage() {
  const { data: clients } = useApi<Client[]>('/api/clients', ClientList);
  const { data: vendorList } = useApi<VendorPickerResponse>(
    '/api/vendors?page_size=100',
    VendorPickerResponseSchema,
  );
  const { data: runsData, refetch: refetchRuns } = useApi<BillingRunsResponse>(
    '/api/billing/runs',
    BillingRunsResponseSchema,
  );
  const [period, setPeriod] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [resolutions, setResolutions] = useState<Record<number, Resolution>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // The QB push is a deliberate step AFTER a successful generate (the CSV is the
  // record). `generated` gates the push button; `pushResult` holds its summary.
  const [generated, setGenerated] = useState(false);
  // The QuickBooks bill-push is gated (T-152) — Eric uploads manually for now.
  const qbBillPush = useFeatureFlag('qb_bill_push');
  const [pushResult, setPushResult] = useState<PushResult | null>(null);
  // Skip / undo / confirm actions on the readiness card (T-210).
  const [readyBusy, setReadyBusy] = useState(false);
  const [readyMsg, setReadyMsg] = useState<string | null>(null);
  // Link to QuickBooks from the readiness card (T-205); scope null = link all unlinked.
  const { admin } = useAuth();
  const isAdmin = admin?.role === 'admin';
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkScope, setLinkScope] = useState<LinkScope | null>(null);
  // Run-driven reports (T-111): which stored run's reports panel is open.
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const selectedRun = runsData?.runs.find((r) => r.id === selectedRunId && r.has_rows) ?? null;

  const allAddressed = useMemo(() => {
    if (!preview) return false;
    return preview.flagged.every((f) => isAddressed(resolutions[f.row_number]));
  }, [preview, resolutions]);

  const canDownload = !!preview && (preview.flagged.length === 0 || allAddressed) && !busy;

  function res(rowNumber: number): Resolution {
    return resolutions[rowNumber] ?? EMPTY_RES;
  }
  function patchRes(rowNumber: number, patch: Partial<Resolution>): void {
    setResolutions((prev) => ({
      ...prev,
      [rowNumber]: { ...EMPTY_RES, ...prev[rowNumber], ...patch },
    }));
  }

  function buildResolutionsPayload(): Record<number, Record<string, string>> {
    const out: Record<number, Record<string, string>> = {};
    for (const [rowNum, r] of Object.entries(resolutions)) {
      const entry: Record<string, string> = {};
      if (r.skip && r.reason.trim()) entry['skip'] = r.reason.trim();
      if (r.product_service.trim()) entry['product_service'] = r.product_service.trim();
      if (r.vendor_name.trim()) entry['vendor_name'] = r.vendor_name.trim();
      if (r.client_id.trim()) entry['client_id'] = r.client_id.trim();
      if (r.worker_id.trim()) entry['worker_id'] = r.worker_id.trim();
      if (Object.keys(entry).length > 0) out[Number(rowNum)] = entry;
    }
    return out;
  }

  async function handlePreview(): Promise<void> {
    if (!file || period.trim() === '') return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('period', period.trim());
      const r = await fetch('/api/billing/preview', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!r.ok) {
        setError(`Preview failed (${r.status}).`);
        return;
      }
      setPreview((await r.json()) as PreviewResult);
      setResolutions({});
      setGenerated(false);
      setPushResult(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  // After linking, re-pull just the readiness card — a full handlePreview would reset the
  // rows Eric has already resolved.
  async function refreshReadiness(): Promise<void> {
    if (!file || period.trim() === '') return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('period', period.trim());
      const r = await fetch('/api/billing/preview', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!r.ok) return;
      const fresh = (await r.json()) as PreviewResult;
      setPreview((p) => (p ? { ...p, qb_readiness: fresh.qb_readiness ?? null } : p));
    } catch {
      // The card just stays as it was; the next Preview refreshes it.
    }
  }

  function openLink(scope: LinkScope | null): void {
    setLinkScope(scope);
    setLinkOpen(true);
  }

  // Each readiness action ends by re-pulling only the card, so resolutions already typed stay put.
  async function runReadyAction(work: () => Promise<string | null>): Promise<void> {
    setReadyBusy(true);
    setReadyMsg(null);
    try {
      setReadyMsg(await work());
    } catch {
      setReadyMsg('That did not go through. Nothing was changed — try again.');
    } finally {
      await refreshReadiness();
      setReadyBusy(false);
    }
  }

  function skipManually(resourceIds: string[]): Promise<void> {
    return runReadyAction(async () => {
      for (const resource_id of resourceIds) {
        await apiMutation<QbSkipResourceInput, { ok: boolean }>('/api/billing/skip-resource', {
          period: period.trim(),
          resource_id,
        });
      }
      return null;
    });
  }

  function undoSkip(resourceId: string): Promise<void> {
    return runReadyAction(async () => {
      await apiMutation<QbUnskipResourceInput, { ok: boolean }>('/api/billing/unskip-resource', {
        period: period.trim(),
        resource_id: resourceId,
      });
      return null;
    });
  }

  // Confirms rate changes from the readiness card or the push result (same endpoint, same rows).
  function confirmRates(rows: ConfirmRow[]): Promise<void> {
    return runReadyAction(async () => {
      const r = await apiMutation<QbConfirmRatesInput, QbConfirmRatesResponse>(
        '/api/billing/confirm-rates',
        {
          confirmations: rows.map((c) => ({
            engagement_id: c.engagement_id,
            field: c.field,
            qb_rate: c.qb_rate,
          })),
        },
        { schema: QbConfirmRatesResponse },
      );
      // Rows that went through (or were already in step) leave the push result's list; the rest stay.
      const stays = new Set(
        r.skipped.filter((k) => k.reason !== 'already up to date').map((k) => `${k.engagement_id}:${k.field}`),
      );
      const done = new Set(
        rows.filter((c) => !stays.has(`${c.engagement_id}:${c.field}`)).map((c) => `${c.engagement_id}:${c.field}`),
      );
      setPushResult((p) =>
        p ? { ...p, changed: p.changed.filter((c) => !done.has(`${c.engagementId}:${c.field}`)) } : p,
      );
      const problems = r.skipped.filter((k) => k.reason !== 'already up to date');
      if (problems.length === 0) return null;
      const lines = problems.map((k) => {
        const row = rows.find((c) => c.engagement_id === k.engagement_id && c.field === k.field);
        return `${row?.worker ?? 'A resource'} — ${FIELD_LABEL[k.field]}: ${k.reason}`;
      });
      return `Not confirmed: ${lines.join('; ')}`;
    });
  }

  async function handleDownload(): Promise<void> {
    if (!file || !preview) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('period', period.trim());
      fd.append('resolutions', JSON.stringify(buildResolutionsPayload()));
      const r = await fetch('/api/billing/generate', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });

      if (r.status === 409) {
        const body = (await r.json()) as { flagged: FlaggedRow[] };
        setPreview((p) =>
          p ? { ...p, flagged: body.flagged, rows_flagged: body.flagged.length } : p,
        );
        setInfo('Some rows still need attention — resolve or skip them, then download again.');
        return;
      }
      if (!r.ok) {
        setError(`Generate failed (${r.status}).`);
        return;
      }

      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${period.trim()}_Updated_Processed_File_Format.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setInfo('Downloaded. The billing run has been recorded.');
      setGenerated(true);
      void refetchRuns();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handlePush(): Promise<void> {
    if (!file || !preview) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('period', period.trim());
      fd.append('resolutions', JSON.stringify(buildResolutionsPayload()));
      const r = await fetch('/api/billing/push-to-qb', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });

      if (r.status === 409) {
        const body = (await r.json()) as { error?: string; flagged?: FlaggedRow[] };
        if (body.error === 'quickbooks_not_connected') {
          // Disconnected partway: show what did go through (and the stop notice) instead.
          if (isPushResult(body)) setPushResult(body);
          else setError('QuickBooks isn’t connected. Connect it in Settings, then push again.');
        } else if (body.flagged) {
          setPreview((p) =>
            p ? { ...p, flagged: body.flagged!, rows_flagged: body.flagged!.length } : p,
          );
          setInfo('Some rows still need attention — resolve or skip them, then push again.');
        } else {
          setError('Push failed (409).');
        }
        return;
      }
      if (!r.ok) {
        const body: unknown = await r.json().catch(() => null);
        if (isPushResult(body)) setPushResult(body);
        else setError(`Push failed (${r.status}).`);
        return;
      }
      setPushResult((await r.json()) as PushResult);
      void refetchRuns();
      // Differences the push just reported stay confirmable from the card too.
      void refreshReadiness();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={s.page}>
      <h1 className={s.title}>Billing</h1>
      <p className={s.subtitle}>
        Upload the raw VMS export (CSV) for a period; the portal maps it to the QuickBooks-ready
        file. Rows it can&apos;t map are listed below to resolve or skip.
      </p>

      <section className={s.section}>
        <div className={s.controls}>
          <div className={s.field}>
            <label className={s.label} htmlFor="bill-period">
              Period
            </label>
            <select
              id="bill-period"
              className={s.input}
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="">Select…</option>
              {BILLING_PERIODS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div className={s.field}>
            <label className={s.label} htmlFor="bill-file">
              VMS export (.csv)
            </label>
            <input
              id="bill-file"
              type="file"
              accept=".csv,text/csv"
              className={s.input}
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setGenerated(false);
                setPushResult(null);
              }}
            />
          </div>
          <button
            type="button"
            className={s.previewBtn}
            disabled={!file || period.trim() === '' || busy}
            onClick={() => void handlePreview()}
          >
            {busy ? 'Working…' : 'Preview'}
          </button>
        </div>
        {error && (
          <div className={s.error} role="alert">
            {error}
          </div>
        )}
        {info && (
          <div className={s.info} role="status">
            {info}
          </div>
        )}
      </section>

      {preview && (
        <>
          <section className={s.section}>
            <div className={s.counts}>
              <span>
                <strong>{preview.rows_in}</strong> rows in
              </span>
              <span>
                <strong>{preview.rows_ready}</strong> ready
              </span>
              <span>
                <strong>{preview.rows_flagged}</strong> flagged
              </span>
              <span>
                <strong>{preview.rows_skipped}</strong> skipped
              </span>
            </div>
          </section>

          {preview.qb_readiness && (
            <ReadinessCard
              rd={preview.qb_readiness}
              isAdmin={isAdmin}
              busy={readyBusy}
              message={readyMsg}
              onLink={openLink}
              onSkip={(ids) => void skipManually(ids)}
              onUndo={(id) => void undoSkip(id)}
              onConfirm={(rows) =>
                void confirmRates(
                  rows.map((c) => ({
                    engagement_id: c.engagement_id,
                    field: c.field,
                    qb_rate: c.qb_rate,
                    worker: c.worker,
                  })),
                )
              }
              onRetry={() => void runReadyAction(async () => null)}
            />
          )}

          {preview.flagged.length > 0 && (
            <section className={s.section}>
              <h2 className={s.sectionTitle}>Resolve missing data</h2>
              <p className={s.sectionNote}>
                Fix a row by supplying the missing Service, or map an unknown worker to a Vendor +
                Client (pick an existing vendor or type a new one; that commits a resource +
                engagement you can finish later). Worker ID is optional. Or skip the row with a
                reason.
              </p>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th scope="col">Row</th>
                    <th scope="col">Worker</th>
                    <th scope="col">Problem(s)</th>
                    <th scope="col">Service</th>
                    <th scope="col">Worker ID</th>
                    <th scope="col">Vendor name</th>
                    <th scope="col">Client</th>
                    <th scope="col">Skip</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.flagged.map((f) => {
                    const r = res(f.row_number);
                    return (
                      <tr key={f.row_number} className={isAddressed(r) ? s.addressed : undefined}>
                        <td>{f.row_number}</td>
                        <td>{f.worker || <span className={s.muted}>—</span>}</td>
                        <td>
                          <ul className={s.problems}>
                            {f.problems.map((p) => (
                              <li key={p}>{p}</li>
                            ))}
                          </ul>
                        </td>
                        <td>
                          <input
                            type="text"
                            className={s.cellInput}
                            aria-label={`Service for row ${f.row_number}`}
                            value={r.product_service}
                            disabled={r.skip}
                            onChange={(e) =>
                              patchRes(f.row_number, { product_service: e.target.value })
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className={s.cellInputNarrow}
                            aria-label={`Worker ID for row ${f.row_number}`}
                            value={r.worker_id}
                            disabled={r.skip}
                            onChange={(e) =>
                              patchRes(f.row_number, { worker_id: e.target.value })
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className={s.cellInput}
                            list="billing-vendor-options"
                            aria-label={`Vendor name for row ${f.row_number}`}
                            value={r.vendor_name}
                            disabled={r.skip}
                            onChange={(e) =>
                              patchRes(f.row_number, { vendor_name: e.target.value })
                            }
                          />
                        </td>
                        <td>
                          <select
                            className={s.cellInput}
                            aria-label={`Client for row ${f.row_number}`}
                            value={r.client_id}
                            disabled={r.skip}
                            onChange={(e) => patchRes(f.row_number, { client_id: e.target.value })}
                          >
                            <option value="">—</option>
                            {clients?.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <label className={s.skipToggle}>
                            <input
                              type="checkbox"
                              aria-label={`Skip row ${f.row_number}`}
                              checked={r.skip}
                              onChange={(e) => patchRes(f.row_number, { skip: e.target.checked })}
                            />
                            skip
                          </label>
                          {r.skip && (
                            <input
                              type="text"
                              className={s.cellInput}
                              aria-label={`Skip reason for row ${f.row_number}`}
                              placeholder="reason"
                              value={r.reason}
                              onChange={(e) => patchRes(f.row_number, { reason: e.target.value })}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <datalist id="billing-vendor-options">
                {vendorList?.items.map((v) => (
                  <option key={v.id} value={v.business_name} />
                ))}
              </datalist>
            </section>
          )}

          {preview.skipped.length > 0 && (
            <section className={s.section}>
              <h2 className={s.sectionTitle}>Skipped ({preview.skipped.length})</h2>
              <ul className={s.skippedList}>
                {preview.skipped.map((sk) => (
                  <li key={sk.row_number}>
                    Row {sk.row_number} — {sk.worker || '—'}: {sk.reason}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className={s.footer}>
            {!canDownload && preview.flagged.length > 0 && (
              <span className={s.gateHint}>
                Resolve or skip every flagged row to enable download.
              </span>
            )}
            <button
              type="button"
              className={s.downloadBtn}
              disabled={!canDownload}
              onClick={() => void handleDownload()}
            >
              {busy ? 'Working…' : 'Download output CSV'}
            </button>
            {generated && qbBillPush && (
              <button
                type="button"
                className={s.pushBtn}
                disabled={busy}
                onClick={() => void handlePush()}
              >
                {busy ? 'Working…' : 'Push to QuickBooks'}
              </button>
            )}
          </div>

          {generated && qbBillPush && !pushResult && (
            <p className={s.sectionNote}>
              Reviewed the CSV? Push this period to QuickBooks to create the bills (one per vendor +
              resource). Re-pushing the same period is safe — it won&apos;t duplicate.
            </p>
          )}

          {pushResult && (
            <section className={s.section}>
              <h2 className={s.sectionTitle}>Pushed to QuickBooks — {pushResult.period}</h2>
              <div className={s.counts}>
                <span>
                  <strong>{pushResult.pushed}</strong> bills pushed
                </span>
                <span>
                  <strong>{pushResult.already_pushed}</strong> already pushed
                </span>
                <span>
                  <strong>{pushResult.held.length}</strong> held
                </span>
                <span>
                  <strong>{pushResult.failed.length}</strong> failed
                </span>
                <span>
                  <strong>{pushResult.skipped_manually.length}</strong> skipped (manual)
                </span>
              </div>
              {pushResult.aborted && (
                <p className={s.error} role="alert">
                  {ABORT_COPY[pushResult.aborted.reason]} {pushResult.aborted.notAttempted}{' '}
                  {pushResult.aborted.notAttempted === 1 ? 'resource was' : 'resources were'} not
                  attempted.
                </p>
              )}
              {pushResult.failed.length > 0 && (
                <>
                  <p className={s.sectionNote}>
                    QuickBooks rejected these bills; the rest of the run carried on. Fix the cause,
                    then push again — bills already pushed won&apos;t be duplicated.
                  </p>
                  <ul className={s.skippedList}>
                    {pushResult.failed.map((f) => (
                      <li key={f.resourceId}>
                        {f.worker || '—'}
                        {f.vendor ? ` (${f.vendor})` : ''}: {f.reason}
                        {f.intuitTid ? ` [QB ref ${f.intuitTid}]` : ''}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {pushResult.filled.length > 0 && (
                <details className={s.readyBlock}>
                  <summary className={s.readySubTitle}>
                    Rates filled from QuickBooks ({pushResult.filled.length})
                  </summary>
                  <ul className={s.skippedList}>
                    {pushResult.filled.map((f) => (
                      <li key={`${f.resourceId}:${f.field}`}>
                        {f.worker || '—'}: {FIELD_LABEL[f.field]} set to {money(f.qbRate)}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {pushResult.changed.length > 0 && (
                <div className={s.readyBlock}>
                  <h3 className={s.readySubTitle}>
                    Rates that differ from QuickBooks ({pushResult.changed.length})
                  </h3>
                  <p className={s.sectionNote}>
                    These bills used QuickBooks&apos; rate. Confirm a change to update the portal to
                    match.
                  </p>
                  <ul className={s.readyList}>
                    {pushResult.changed.map((c) => (
                      <li key={`${c.engagementId}:${c.field}`} className={s.readyRow}>
                        <span className={s.readyVendor}>{c.worker || '—'}</span>
                        <span className={s.readyReason}>
                          {FIELD_LABEL[c.field]} {money(c.portalRate)} → {money(c.qbRate)} (
                          {pctText(c.changePct)})
                        </span>
                        {isAdmin && (
                          <button
                            type="button"
                            className={s.readyLinkBtn}
                            aria-label={`Confirm ${c.worker} ${FIELD_LABEL[c.field]} from the push result`}
                            disabled={readyBusy}
                            onClick={() => void confirmRates([pushChangeRow(c)])}
                          >
                            Confirm
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                  {isAdmin && (
                    <div className={s.readyActions}>
                      <button
                        type="button"
                        className={s.previewBtn}
                        disabled={readyBusy}
                        onClick={() => void confirmRates(pushResult.changed.map(pushChangeRow))}
                      >
                        Confirm all
                      </button>
                    </div>
                  )}
                  {readyMsg && (
                    <p className={s.error} role="alert">
                      {readyMsg}
                    </p>
                  )}
                </div>
              )}
              {hiddenClientRatesNote(
                pushResult.hidden_client_rates.changed,
                pushResult.hidden_client_rates.filled,
              ) && (
                <p className={`${s.sectionNote} ${s.muted}`}>
                  {hiddenClientRatesNote(
                    pushResult.hidden_client_rates.changed,
                    pushResult.hidden_client_rates.filled,
                  )}
                </p>
              )}
              {pushResult.held.length > 0 && (
                <>
                  <p className={s.sectionNote}>
                    Held resources aren&apos;t in QuickBooks yet (something needs fixing first, such as
                    an unlinked vendor, service or customer, or a rate that needs checking). Fix it
                    or mark it handled manually, then push again.
                  </p>
                  <ul className={s.skippedList}>
                    {pushResult.held.map((h) => (
                      <li key={h.resourceId}>
                        {h.worker || '—'}: {h.reason}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>
          )}
        </>
      )}

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Recent billing runs</h2>
        <p className={s.sectionNote}>
          Output rows are stored with each run — select a run to re-download its output or pull
          referral reports, no re-upload needed.
        </p>
        {!runsData || runsData.runs.length === 0 ? (
          <p className={s.sectionNote}>No billing runs yet.</p>
        ) : (
          <ul className={s.runsList}>
            {runsData.runs.map((run) => (
              <li
                key={run.id}
                className={`${s.runItem} ${run.has_rows ? s.runItemSelectable : ''} ${
                  run.id === selectedRunId ? s.runItemSelected : ''
                }`}
              >
                <div className={s.runHead}>
                  <strong>{run.period}</strong>
                  <span className={s.runMeta}>{run.created_at.slice(0, 10)}</span>
                  <span className={s.runMeta}>
                    {run.rows_out} out · {run.rows_skipped} skipped
                  </span>
                  {run.has_rows ? (
                    <button
                      type="button"
                      className={s.runSelectBtn}
                      onClick={() =>
                        setSelectedRunId((id) => (id === run.id ? null : run.id))
                      }
                    >
                      {run.id === selectedRunId ? 'Hide reports' : 'Reports'}
                    </button>
                  ) : (
                    <span className={s.runMeta}>no stored rows (pre-upgrade run)</span>
                  )}
                </div>
                {run.skips.length > 0 && (
                  <ul className={s.runSkips}>
                    {run.skips.map((sk) => (
                      <li key={sk.row_number}>
                        Row {sk.row_number} — {sk.worker || '—'}: {sk.reason}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {selectedRun && <RunReportsPanel run={selectedRun} />}
      {isAdmin && (
        <LinkReviewDialog
          open={linkOpen}
          onOpenChange={setLinkOpen}
          scope={linkScope}
          onApplied={() => void refreshReadiness()}
        />
      )}
    </div>
  );
}

function pushChangeRow(c: PushResult['changed'][number]): ConfirmRow {
  return { engagement_id: c.engagementId, field: c.field, qb_rate: c.qbRate, worker: c.worker };
}

function ReadinessCard({
  rd,
  isAdmin,
  busy,
  message,
  onLink,
  onSkip,
  onUndo,
  onConfirm,
  onRetry,
}: {
  rd: QbReadiness;
  isAdmin: boolean;
  busy: boolean;
  message: string | null;
  onLink: (scope: LinkScope | null) => void;
  onSkip: (resourceIds: string[]) => void;
  onUndo: (resourceId: string) => void;
  onConfirm: (rows: QbReadiness['rate_changes']) => void;
  onRetry: () => void;
}) {
  const heldTotal = rd.held.reduce((n, h) => n + h.resource_count, 0);
  const anyLinkable = rd.held.some((h) => h.linkable);
  const hiddenNote = hiddenClientRatesNote(rd.hidden_client_rates.changes, rd.hidden_client_rates.fills);

  return (
    <section className={s.section} aria-label="QuickBooks readiness">
      <h2 className={s.sectionTitle}>
        QuickBooks readiness{' '}
        {rd.held.length > 0 ? (
          <span className={s.readyHeld}>{`${heldTotal} of ${rd.total_resources} would be held`}</span>
        ) : rd.rates_checked ? (
          <span className={s.readyOk}>All clear</span>
        ) : (
          <span className={s.readyHeld}>Rates not fully checked</span>
        )}
      </h2>

      {rd.held.length === 0 ? (
        <p className={s.sectionNote}>
          {!rd.rates_checked
            ? 'Every resource in this file is linked, but its rates have not all been checked yet (see below).'
            : rd.skipped.length === 0
              ? `All ${rd.total_resources} resources in this file are linked and will be pushed.`
              : `Everything else in this file is ready and will be pushed; ${rd.skipped.length} handled manually.`}
        </p>
      ) : (
        <>
          <p className={s.sectionNote}>
            These resources have hours in this file but can&apos;t be pushed yet. Everything else
            will push normally; held ones are skipped, not failed. Prefer to enter one in
            QuickBooks yourself? Mark it as handled manually and no later push will bill it again.
          </p>
          <ul className={s.readyList}>
            {rd.held.map((h) => (
              <li key={`${h.vendor_id}:${h.reason}`} className={s.readyRow}>
                <span className={s.readyVendor}>{h.vendor}</span>
                <span className={s.readyReason}>{h.reason}</span>
                <span className={s.readyCount}>
                  {`${h.resource_count} resource${h.resource_count === 1 ? '' : 's'}`}
                </span>
                {isAdmin && h.linkable && (
                  <button
                    type="button"
                    className={s.readyLinkBtn}
                    aria-label={`Link ${h.vendor} to QuickBooks`}
                    onClick={() => onLink({ vendorId: h.vendor_id, vendorName: h.vendor })}
                  >
                    Link…
                  </button>
                )}
                {isAdmin && (
                  <button
                    type="button"
                    className={s.readyLinkBtn}
                    aria-label={`Handle ${h.vendor} manually`}
                    disabled={busy}
                    onClick={() => onSkip(h.resources.map((r) => r.resource_id))}
                  >
                    {h.resources.length === 1
                      ? "I'll do this one manually"
                      : `Skip these ${h.resources.length} manually`}
                  </button>
                )}
              </li>
            ))}
          </ul>
          {isAdmin && anyLinkable && (
            <div className={s.readyActions}>
              <button type="button" className={s.previewBtn} onClick={() => onLink(null)}>
                Link all to QuickBooks
              </button>
            </div>
          )}
        </>
      )}

      {!rd.rates_checked && (
        <div className={s.readyBlock} role="status">
          <p className={s.sectionNote}>
            {rd.rates_unchecked > 0 && rd.rates_unchecked < rd.total_resources
              ? `Couldn't check rates for ${rd.rates_unchecked} of ${rd.total_resources} resources — QuickBooks is busy or unreachable.`
              : "Rates weren't checked — QuickBooks is busy or unreachable."}{' '}
            Rate changes and holds below may be incomplete. Pushing is still safe (it re-reads
            QuickBooks), but you may not see every change to confirm.
          </p>
          {isAdmin && (
            <div className={s.readyActions}>
              <button type="button" className={s.previewBtn} disabled={busy} onClick={onRetry}>
                Check rates again
              </button>
            </div>
          )}
        </div>
      )}

      {rd.rate_changes.length > 0 && (
        <div className={s.readyBlock}>
          <h3 className={s.readySubTitle}>Rate changed on QuickBooks ({rd.rate_changes.length})</h3>
          <p className={s.sectionNote}>
            QuickBooks&apos; rate is what gets billed. Confirm a change to update the portal to
            match
            {rd.rate_changes.some((c) => c.hold)
              ? '; changes held for confirmation stay held until you do.'
              : '.'}
          </p>
          <ul className={s.readyList}>
            {rd.rate_changes.map((c) => (
              <li
                key={`${c.engagement_id}:${c.field}`}
                className={`${s.readyRow} ${c.hold ? s.readyRowHold : ''}`}
              >
                <span className={s.readyVendor}>
                  {c.worker || '—'} <span className={s.muted}>({c.vendor})</span>
                </span>
                <span className={s.readyReason}>
                  {FIELD_LABEL[c.field]}: {money(c.portal_rate)} → {money(c.qb_rate)} ({pctText(c.change_pct)})
                </span>
                <span className={c.hold ? s.readyHoldText : s.readyCount}>
                  {c.pushed
                    ? "already billed at QuickBooks' rate; confirm to update the portal"
                    : c.hold
                      ? `held until confirmed (over ±${RATE_CHANGE_HOLD_PCT}%)`
                      : "billed at QuickBooks' rate; confirm to update the portal"}
                </span>
                {isAdmin && (
                  <button
                    type="button"
                    className={s.readyLinkBtn}
                    aria-label={`Confirm ${c.worker} ${FIELD_LABEL[c.field]}`}
                    disabled={busy}
                    onClick={() => onConfirm([c])}
                  >
                    Confirm
                  </button>
                )}
              </li>
            ))}
          </ul>
          {isAdmin && (
            <div className={s.readyActions}>
              <button
                type="button"
                className={s.previewBtn}
                disabled={busy}
                onClick={() => onConfirm(rd.rate_changes)}
              >
                Confirm all
              </button>
            </div>
          )}
        </div>
      )}

      {rd.rate_fills.length > 0 && (
        <details className={s.readyBlock}>
          <summary className={s.readySubTitle}>
            Portal rate blank — will be filled from QuickBooks when you push ({rd.rate_fills.length})
          </summary>
          <ul className={s.readyList}>
            {rd.rate_fills.map((f) => (
              <li key={`${f.engagement_id}:${f.field}`} className={s.readyRow}>
                <span className={s.readyVendor}>
                  {f.worker || '—'} <span className={s.muted}>({f.vendor})</span>
                </span>
                <span className={s.readyReason}>
                  {FIELD_LABEL[f.field]}: {money(f.qb_rate)}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {hiddenNote && <p className={`${s.sectionNote} ${s.muted}`}>{hiddenNote}</p>}

      {rd.skipped.length > 0 && (
        <div className={s.readyBlock}>
          <h3 className={s.readySubTitle}>Handled manually ({rd.skipped.length})</h3>
          <ul className={s.readyList}>
            {rd.skipped.map((k) => (
              <li key={k.resource_id} className={s.readyRow}>
                <span className={s.readyVendor}>
                  {k.worker || '—'} <span className={s.muted}>({k.vendor})</span>
                </span>
                <span className={s.readyReason}>{k.note ?? ''}</span>
                {isAdmin && (
                  <button
                    type="button"
                    className={s.readyLinkBtn}
                    aria-label={`Undo handled manually for ${k.worker}`}
                    disabled={busy}
                    onClick={() => onUndo(k.resource_id)}
                  >
                    Undo
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {message && (
        <p className={s.error} role="alert">
          {message}
        </p>
      )}
    </section>
  );
}
